import { useSelector } from 'react-redux';
import moment from 'moment';

import { postClarityLeadSourceData } from 'api/clarity';
import { getAgentTeamIdSelector } from 'store/selectors/agentTeamDetail';
import {
  getAgentTeamStatsSelector,
  getTimePeriodDueFromSelector,
  getTimePeriodDueToSelector,
  getTimePeriodSelector,
  getTransactionFilter,
} from 'store/selectors/clarity';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { TEAM_BASIC } from 'settings/constants/roles';
import { getUserId } from 'store/selectors/user';
import { assignRandomColor, formatAgentUserContact } from './helper';

type VolumeStatsProps = {
  setSummary: (val) => void;
  setData: (val) => void;
  setIsPending: (val) => void;
  aggregateDataFormat: string;
  year?: number | null;
  quarter?: number | null;
  month?: number | null;
  isoWeek?: number | null;
  date?: string | null;
  expand?: boolean;
  agentSummary?: boolean;
  transactionAgentUserId?: boolean;
  transactionAgentContactId?: boolean;
  isTeam?: boolean;
  sourceSummary?: boolean;
  sourceList?: string[];
};

export const useFetchLeadSourceStats = () => {
  const teamId = useSelector(getAgentTeamIdSelector);
  const teamStats = useSelector(getAgentTeamStatsSelector);
  const timePeriod = useSelector(getTimePeriodSelector);
  const transactionFilter = useSelector(getTransactionFilter);
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const userId = useSelector(getUserId);
  const dueFrom = useSelector(getTimePeriodDueFromSelector);
  const dueTo = useSelector(getTimePeriodDueToSelector);

  const getSummaryType = () => {
    switch (transactionFilter) {
      case 0:
        return 'All Transactions';
      case 1:
        return 'Listings';
      case 2:
        return 'Purchases';
    }
  };
  const fetchLeadSourceStats = async ({
    setSummary,
    setData,
    setIsPending,
    aggregateDataFormat,
    year,
    quarter,
    month,
    isoWeek,
    date,
    expand = false,
    agentSummary = false,
    transactionAgentUserId = false,
    transactionAgentContactId = false,
    isTeam = false,
    sourceSummary = false,
    sourceList = [] as string[],
  }: VolumeStatsProps) => {
    setIsPending(true);
    const previousYear = moment().subtract(1, 'year').year();
    if (timePeriod === previousYear) year = previousYear;
    const excludeTimePeriod = !!(quarter || month || isoWeek || dueFrom || dueTo);
    const sendValues = {
      teamId: teamId,
      ...(!excludeTimePeriod && { timePeriod: timePeriod === previousYear ? 'YTD' : timePeriod }),
      aggregateDataFormat,
      summaryType: getSummaryType(),
      ...(agentRole === TEAM_BASIC && { transactionAgentUserId: userId }),
      ...(teamStats.itemValue !== 'Team' && { transactionAgentUserId: teamStats.userId }),
      ...(expand && { expand: expand }),
      ...(year && { year: year }),
      ...(quarter && { quarter }),
      ...(month && { month }),
      ...(isoWeek && { isoWeek }),
      ...(date && { date }),
      ...(agentSummary && { agentSummary: agentSummary }),
      ...(!isTeam && transactionAgentUserId && { transactionAgentUserId: transactionAgentUserId }),
      ...(!isTeam &&
        !transactionAgentUserId &&
        transactionAgentContactId && { transactionAgentContactId: transactionAgentContactId }),
      ...(sourceSummary && { sourceSummary: sourceSummary }),
      ...(sourceList.length && { sourceList: sourceList }),
      ...(dueFrom && { dueFrom: dueFrom.format('MM-DD-YY') }),
      ...(dueTo && { dueTo: dueTo.format('MM-DD-YY') }),
    };
    const { data, status } = await postClarityLeadSourceData(sendValues);
    if (status === 200) {
      if (agentSummary) {
        setData(data.Summary || []);
      } else if (expand) {
        setSummary(data.Summary || {});
        setData(data.Data || []);
      } else if (sourceList.length) {
        setSummary(data.Summary || {});
        setData(data.Data || []);
      } else {
        setSummary(data.Summary || {});
        setData(assignRandomColor(data.Data) || []);
      }
    }
    setIsPending(false);
  };
  return fetchLeadSourceStats;
};
